import { Button } from '#/components/Button/Button';
import { FilestackImage } from '#/components/FilestackImage/FilestackImage';
import { RichText } from '#/components/Text/RichText';
import { RichTextHeadline } from '#/components/Text/RichTextHeadline';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { CmsApiCtaHeroBlockData } from '#/modules/cms/api/types/blocks/CmsApiCtaHeroBlock';
import {
  CmsApiCtaHeroBlockLayout,
  CmsApiCtaHeroBlockTextColor,
} from '#/modules/cms/api/types/blocks/CmsApiCtaHeroBlock';
import { colors } from '#/theme/theme';
import { trackAnchorClicked } from '#/utils/analytics/track/trackAnchorClicked';

import {
  CmsCtaHeroBackgroundHolder,
  CmsCtaHeroBackgroundImageHolder,
  CmsCtaHeroBlockHolder,
} from './CmsCtaHeroBlock.styled';

type Props = {
  data: CmsApiCtaHeroBlockData;
  isPageHeader?: boolean;
  blockIndex: number;
  pageUpdated: string;
};

export function CmsCtaHeroBlock({
  data: {
    buttonText,
    buttonUrl,
    text,
    bodyText,
    textColor,
    image,
    withOverlay,
    layout,
  },
  isPageHeader,
  blockIndex,
  pageUpdated,
}: Props) {
  const { i18n } = useI18n();

  const color =
    textColor === CmsApiCtaHeroBlockTextColor.DARK_CONTENT_GREY
      ? colors.darkContentGrey
      : colors.white;

  return (
    <CmsCtaHeroBlockHolder
      data-qa-id="call-to-action-hero"
      data-layout={layout}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <CmsCtaHeroBackgroundHolder>
          <CmsCtaHeroBackgroundImageHolder>
            <FilestackImage
              qaId="hero-image-image"
              width={1920}
              height={layout === CmsApiCtaHeroBlockLayout.STANDARD ? 1024 : 200}
              expandSize
              objectFit="cover"
              alt={i18n('Hero image')}
              data={image}
              mobileAspectRatio={{ width: 414, height: 470 }}
              eagerLoad={blockIndex <= 1}
              style={{ height: '100%' }}
            />
          </CmsCtaHeroBackgroundImageHolder>
        </CmsCtaHeroBackgroundHolder>

        {withOverlay && (
          <div
            style={{
              position: 'absolute',
              transform: 'translateZ(0)',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'rgb(0 0 0 / 0.4)',
            }}
          />
        )}
      </div>

      <div
        style={{
          position: 'relative',
          transform: 'rotateZ(0)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div
          style={{ maxWidth: '768px', margin: '0 auto', paddingInline: '20px' }}
        >
          <RichTextHeadline
            color={color}
            textAlignCenter
            tag={isPageHeader ? 'h1' : undefined}
            styleAs={isPageHeader ? 'h2' : undefined}
            $reverseUnderline
            $noMarginBottom={!(buttonUrl && buttonText)}
          >
            {text}
          </RichTextHeadline>
          {bodyText && (
            <div
              style={{
                marginTop: '36px',
                marginBottom: '40px',
                textAlign: 'center',
              }}
            >
              <RichText $color={color} html={bodyText} />
            </div>
          )}
        </div>

        {buttonUrl && buttonText && (
          <div style={{ display: 'flex', marginTop: bodyText ? '0px' : '4px' }}>
            <Button
              variant="primary"
              type="link"
              href={buttonUrl}
              onClick={(event) =>
                trackAnchorClicked(event, 'CMS Block', {
                  block_position: blockIndex,
                  block_type: 'Call to Action Hero',
                  cta_type: 'Button',
                  button_text: buttonText,
                  page_updated: pageUpdated,
                })
              }
            >
              {buttonText}
            </Button>
          </div>
        )}
      </div>
    </CmsCtaHeroBlockHolder>
  );
}
